import { ParticipantWithBib, BibPolicy, CategoryGroup, BibStats } from "../types/bib.types";

// ─── Sorting ─────────────────────────────────────────────────────────────────

export const sortParticipantsByPolicy = (
  participants: ParticipantWithBib[],
  policy: BibPolicy
): ParticipantWithBib[] => {
  const sorted = [...participants];
  switch (policy) {
    case "registration_date":
      return sorted.sort(
        (a, b) =>
          new Date(a.registration_date).getTime() -
          new Date(b.registration_date).getTime()
      );
    case "gender":
      return sorted.sort((a, b) => {
        const order = { male: 0, female: 1, other: 2 };
        return (order[a.gender ?? "other"] ?? 2) - (order[b.gender ?? "other"] ?? 2);
      });
    case "first_name":
      return sorted.sort((a, b) =>
        (a.first_name ?? "").localeCompare(b.first_name ?? "")
      );
    case "last_name":
      return sorted.sort((a, b) =>
        (a.last_name ?? "").localeCompare(b.last_name ?? "")
      );
    case "gender_age":
      return sorted.sort((a, b) => {
        const genderOrder = { male: 0, female: 1, other: 2 };
        const gDiff =
          (genderOrder[a.gender ?? "other"] ?? 2) -
          (genderOrder[b.gender ?? "other"] ?? 2);
        if (gDiff !== 0) return gDiff;
        return (a.age ?? 0) - (b.age ?? 0);
      });
    default:
      return sorted;
  }
};

export const sortParticipantsByRegistrationDate = (
  participants: ParticipantWithBib[]
): ParticipantWithBib[] => sortParticipantsByPolicy(participants, "registration_date");

// ─── BIB Assignment ───────────────────────────────────────────────────────────

export const assignSequentialBibs = (
  participants: ParticipantWithBib[],
  startNumber: number
): ParticipantWithBib[] =>
  participants.map((p, i) => ({ ...p, bib_number: startNumber + i, bib_status: "assigned" as const }));

// ─── Validation ───────────────────────────────────────────────────────────────

export const validateBibRange = (
  start: number,
  end: number,
  participantCount: number
): { isValid: boolean; error?: string } => {
  if (!start || !end) return { isValid: false, error: "Start and end BIB numbers are required" };
  if (start < 1) return { isValid: false, error: "Start BIB must be at least 1" };
  if (start >= end) return { isValid: false, error: "Start BIB must be less than end BIB" };
  if (end - start + 1 < participantCount)
    return {
      isValid: false,
      error: `Range (${end - start + 1}) is smaller than participant count (${participantCount})`,
    };
  return { isValid: true };
};

// ─── Grouping ─────────────────────────────────────────────────────────────────

export const groupParticipantsByCategory = (
  participants: ParticipantWithBib[]
): CategoryGroup[] => {
  const map = new Map<string, CategoryGroup>();

  participants.forEach((p) => {
    const key = p.ticket_id || p.ticket_name || "unknown";
    if (!map.has(key)) {
      map.set(key, {
        category: p.ticket_name || "Unknown",
        distance: p.distance,
        ticket_id: p.ticket_id,
        participants: [],
        assigned_count: 0,
      });
    }
    const group = map.get(key)!;
    group.participants.push(p);
    if (p.bib_number) group.assigned_count++;
  });

  return Array.from(map.values());
};

// ─── Stats ────────────────────────────────────────────────────────────────────

export const computeBibStats = (participants: ParticipantWithBib[]): BibStats => {
  const stats: BibStats = { total: participants.length, male: 0, female: 0, other: 0, categories: [] };
  const catMap = new Map<string, { count: number; distance?: string }>();

  participants.forEach((p) => {
    if (p.gender === "male") stats.male++;
    else if (p.gender === "female") stats.female++;
    else stats.other++;

    const catKey = p.ticket_name || "Unknown";
    const existing = catMap.get(catKey);
    if (existing) existing.count++;
    else catMap.set(catKey, { count: 1, distance: p.distance });
  });

  stats.categories = Array.from(catMap.entries()).map(([name, v]) => ({
    name,
    count: v.count,
    distance: v.distance,
  }));

  return stats;
};

// ─── Misc ─────────────────────────────────────────────────────────────────────

export const bibStatusColor = (status?: string): string => {
  switch (status) {
    case "authorized": return "green";
    case "assigned": return "blue";
    case "rejected": return "red";
    default: return "default";
  }
};
